import React, { useMemo } from 'react';
import { Plus, Trash2 } from 'shared/assets/icons';
import { DatepickerSelect } from 'shared/components/ui/datepicker-select';
import { FormInput } from 'shared/components/ui/form-input';
import { IconButton } from 'shared/components/ui/icon-button';
import { useFormattedNumericInput } from 'shared/hooks/use-formatted-numeric-input';
import { useNumberFormat } from 'shared/hooks/use-number-format';
import {
  DateFormatOptions,
  dateFormatOptionToLabelMap,
} from 'shared/types/date-format-options';
import { MetricEntry, MetricEntryMode } from 'shared/types/goal-metric';
import { ID } from 'shared/types/id';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { calculateMetricEntryValue } from 'shared/utils/calculate-metric-entry-value';
import { isNumber } from 'shared/utils/is-number';
import { DefaultTheme } from 'styled-components';

import * as Styled from './goal-progress-history-form.style';

type GoalProgressHistoryEntryProps = {
  entry: MetricEntry;
  sortedEntries: MetricEntry[];
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  startValue: number;
  localizedSymbol?: string;
  theme: DefaultTheme;
  onUpdateValue: (id: ID, value: number) => void;
  onUpdateDate: (id: ID, date: Timestamp) => void;
  onDelete: (id: ID) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
};

export const GoalProgressHistoryEntry: React.FC<
  GoalProgressHistoryEntryProps
> = ({
  entry,
  sortedEntries,
  dateFormat,
  weekStartsOn,
  startValue,
  localizedSymbol,
  theme,
  onUpdateValue,
  onUpdateDate,
  onDelete,
  onKeyDown,
}) => {
  const isRelativeEntry = entry.mode === MetricEntryMode.Relative;
  const computedTotal = calculateMetricEntryValue(
    entry.id,
    sortedEntries,
    startValue ?? 0,
  );

  const separators = useNumberFormat();
  const { formattedValue, onInputChange, onInputBlur, onInputPaste } =
    useFormattedNumericInput({
      value: entry.value,
      onChange: (value) => onUpdateValue(entry.id, value),
    });

  const formattedTotal = useMemo(() => {
    if (!isNumber(computedTotal)) {
      return '';
    }
    const stringValue = computedTotal.toString();
    const [integerPart, decimalPart] = stringValue.split('.');

    let finalValue = integerPart.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      separators.group,
    );
    if (decimalPart !== undefined) {
      finalValue += separators.decimal + decimalPart;
    }
    return finalValue;
  }, [computedTotal, separators]);

  const updateDate = (date?: Timestamp) => date && onUpdateDate(entry.id, date);

  return (
    <Styled.TableRow>
      <Styled.TableCell>
        <DatepickerSelect
          value={entry.date}
          dateFormat={dateFormat}
          weekStartsOn={weekStartsOn}
          placeholder={dateFormatOptionToLabelMap[dateFormat]}
          onChange={updateDate}
          isFullWidth
        />
      </Styled.TableCell>

      <Styled.TableCell>
        {isRelativeEntry && (
          <FormInput
            value={formattedValue}
            inputMode="decimal"
            type="text"
            iconLeft={Plus}
            iconLeftColor={theme.palette.grey[600]}
            postFix={localizedSymbol}
            onKeyDown={onKeyDown}
            onChange={onInputChange}
            onBlur={onInputBlur}
            onPaste={onInputPaste}
            isSmall
            hideNumberButtons
          />
        )}
      </Styled.TableCell>

      <Styled.TableCell>
        <FormInput
          // In Absolute mode, this field is editable and mirrors the behavior of the "Increment" field
          // In Relative mode, it is read-only and shows the calculated total
          value={isRelativeEntry ? formattedTotal : formattedValue}
          inputMode="decimal"
          type="text"
          onKeyDown={isRelativeEntry ? undefined : onKeyDown}
          onChange={isRelativeEntry ? undefined : onInputChange}
          onBlur={isRelativeEntry ? undefined : onInputBlur}
          onPaste={isRelativeEntry ? undefined : onInputPaste}
          disabled={isRelativeEntry}
          isSmall
          hideNumberButtons
        />
      </Styled.TableCell>

      <Styled.TableCell $isInteraction>
        <IconButton icon={Trash2} onClick={() => onDelete(entry.id)} />
      </Styled.TableCell>
    </Styled.TableRow>
  );
};
